import React, { useState, useEffect } from "react";
import { WidgetProps, TankLevelSettings } from "./../DashboardContext";
import { isMultiValue, KaSelect } from "../../Common/KaSelect";
import useGetTankLevels, { TankDto } from "../../../requests/useGetTankLevels";
import useInterval from "../../Common/useInterval";
import KaModal from "../../Common/KaModal";
import KaAlert from "../../Common/KaAlert";
import useDisplayMessage from "../../Common/useDisplayMessage";
import useUpdateTankLevelWidgetSettings from "../../../requests/useUpdateTankLevelWidgetSettings";
import { Widget } from "./../WidgetV2";
import SiteDto from "../../Common/dtos/SiteDto";
import { TankLevelDisplay } from "./TankLevelDisplay";
import { getSitesFromTanks } from "./TankLevelPage";
import { humanReadableSort } from "../../../Utilities";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

type SettingsProps = {
  widgetId: string;
  settings: TankLevelSettings;
  settingsUpdated: (settings: TankLevelSettings) => void;
  sites: SiteDto[];
  tanks: TankDto[];
  show: boolean;
  onClose: () => void;
}

const Settings = (props: SettingsProps) => {
  const displayMessage = useDisplayMessage();
  const [settings, setSettings] = useState<TankLevelSettings>({
    name: props.settings.name,
    selectedSiteId: props.settings.selectedSiteId,
    selectedTankIds: props.settings.selectedTankIds ?? ['', '', '', '']
  });

  useEffect(() => {
    update({ selectedTankIds: props.settings.selectedTankIds });
  }, [props.settings.selectedTankIds])

  const updateSettings = useUpdateTankLevelWidgetSettings(props.widgetId, {
    onSuccess: () => {
      props.settingsUpdated(settings);
      props.onClose();
    },
    onError: (err) => displayMessage.fail(err.message)
  });

  const update = (newSettings: Partial<TankLevelSettings>) =>
    setSettings({ ...settings, ...newSettings });

  const siteOptions = props.sites.map(s => ({ value: s.id, label: s.name }));
  const filteredTanks = humanReadableSort(props.tanks.filter(t => t.siteId === settings.selectedSiteId), 'name');

  const getTankOptionsForIndex = (currentIndex: number) => {
    const selectedInOtherDropdowns = settings.selectedTankIds!
      .filter((id, idx) => idx !== currentIndex && id !== '');

    return filteredTanks.map(t => ({
      value: t.id,
      label: `${t.name} - ${t.productName}`,
      isDisabled: selectedInOtherDropdowns.includes(t.id)
    }));
  };

  const saveSettings = () => {
    updateSettings.request({
      name: settings.name,
      selectedSiteId: settings.selectedSiteId,
      selectedTankIds: settings.selectedTankIds!.filter((id) => id !== '')
    });
  };

  const updateTanks = (index: number, id: string) => {
    const tanks = [...settings.selectedTankIds!];
    tanks[index] = id;
    update({ selectedTankIds: tanks });
  }

  const onSiteChange = (siteId: string | undefined) => {
    update({
      selectedSiteId: siteId,
      selectedTankIds: ['', '', '', '']
    });
  }

  return (<KaModal
    onHide={props.onClose}
    show={props.show}
    title={'Tank Level Monitoring Settings'}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <h5>Widget Name:</h5>
      <input
        type="text"
        className="form-control"
        placeholder="Enter Widget Name"
        value={settings.name}
        onChange={(e) => update({ name: e.target.value })} />
      <div className="row mt-3">
        <div className="col">
          <h5>Filter by Site:</h5>
          <KaSelect
            placeholder="Select Site"
            options={siteOptions}
            onChange={(valueObject) => {
              if (!isMultiValue(valueObject)) { onSiteChange(valueObject?.value) }
            }}
            value={siteOptions.find((option) => option.value === settings.selectedSiteId)}
          />
        </div>
      </div>
      <div className="d-flex justify-content-center mt-4">
        <div className="w-75">
          <h5>Select up to 4 Tanks:</h5>
          {Array.from({ length: 4 }, (_, i) => (
            <div className={i === 3 ? "" : "pb-3"} key={i}>
              <KaSelect
                placeholder="Select Tank"
                isClearable={true}
                isDisabled={!settings.selectedSiteId}
                options={getTankOptionsForIndex(i)}
                value={getTankOptionsForIndex(i).find(o => o.value === settings.selectedTankIds![i]) ?? null}
                onChange={(valueObject) => {
                  if (!isMultiValue(valueObject)) { updateTanks(i, valueObject?.value ?? ''); }
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </>)}
    footer={(
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => saveSettings()}>
        Save
      </button>)}
  />)
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  if (date.toDateString() === today.toDateString())
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return date.toLocaleDateString();
};

const WidgetTankDisplay = ({ tank }: { tank?: TankDto }) =>
  <div className="col pe-0">
    {tank &&
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        popperConfig={{ strategy: 'fixed' }}
        overlay={
          <Tooltip style={{ '--bs-tooltip-max-width': '350px', position: 'fixed' } as React.CSSProperties}>
            <div>{tank.name} - {tank.productName}</div>
            <div>Last Reported: {tank.lastSensorReading ? formatDate(tank.lastSensorReading) : 'N/A'}</div>
          </Tooltip>
        }>
        <div>
          <TankLevelDisplay
            percentage={Math.round(tank.percentFilled)}
            label={tank.name + " - " + tank.productName}
            showCountUp={true} />
        </div>
      </OverlayTrigger>
    }
  </div>

type TankLevelProps = WidgetProps & TankLevelSettings;

export const TankLevelWidget = (props: TankLevelProps) => {
  const [settings, setSettings] = useState<TankLevelSettings>({
    name: props.name,
    selectedSiteId: props.selectedSiteId,
    selectedTankIds: props.selectedTankIds ?? [],
  });

  const getTankLevels = useGetTankLevels();
  useInterval(() => getTankLevels.query(), 30_000, false);

  const updateSettings = (s: TankLevelSettings) => {
    setSettings({
      ...s,
      selectedTankIds: s.selectedTankIds!.filter((id) => id !== '')
    });
  }

  const selectedTanks = settings.selectedTankIds
    ?.map(id => getTankLevels.data?.find(t => t.id === id))
    .filter((tank): tank is TankDto => tank !== undefined) ?? [];

  return <Widget
    widgetId={props.id}
    title={settings.name}
    path='/tankLevelMonitoring'
    onLoadQueries={{ tanks: getTankLevels }}
    dataQuery={getTankLevels}
    settings={({ tanks }, show, onClose) =>
      <Settings
        widgetId={props.id.toString()}
        sites={getSitesFromTanks(tanks)}
        tanks={tanks}
        settings={{ ...settings, selectedTankIds: selectedTanks.map(t => t.id) }}
        settingsUpdated={updateSettings}
        show={show}
        onClose={onClose} />
    }
    render={() =>
      selectedTanks.length === 0
        ? <div className="d-flex justify-content-center">
          <h2 className="h-100 w-100 d-flex justify-content-center align-items-center pb-5 text-wrap" style={{ color: 'darkslategrey' }}>
            Please select tanks to monitor
          </h2>
        </div>
        : <div className="h-100 w-100 px-3">
          <div className="row mb-2 px-3 mt-2">
            <WidgetTankDisplay tank={selectedTanks[0]} />
            <WidgetTankDisplay tank={selectedTanks[1]} />
          </div>
          <div className="row mb-2 px-3">
            <WidgetTankDisplay tank={selectedTanks[2]} />
            <WidgetTankDisplay tank={selectedTanks[3]} />
          </div>
        </div>
    }
  />
}
